import { create } from 'zustand';

import { registerStoreReset } from '../lib/sessionReset';
import { supabase } from '../lib/supabase';

// Wiersz z `moduly_z_dostepem(uuid)` (migracja 674): identyfikator organu to
// DECYZJA (jest na liście = organizacja ma dostęp), a `status` i
// `trial_ends_at` są tylko treścią plakietki przy kaflu (`AdminLayout`).
// Reguły dostępu z tych dwóch pól NIE liczy się już nigdzie na kliencie —
// właśnie dlatego lista przychodzi z bazy gotowa.
interface ModuleSubscription {
  module_id: string;
  status: string;
  trial_ends_at: string | null;
}

interface ModuleSubscriptionState {
  subscriptions: ModuleSubscription[];
  isLoading: boolean;
  organizationId: string | null;

  animatingModules: Set<string>;

  setOrganizationId: (orgId: string | null) => void;
  fetchSubscriptions: () => Promise<void>;
  refreshAfterToggle: (moduleId: string, action: 'enable' | 'disable') => Promise<void>;
  clearAnimatingModule: (moduleId: string) => void;
  reset: () => void;
}

const initialState = {
  subscriptions: [] as ModuleSubscription[],
  isLoading: false,
  organizationId: null as string | null,
  animatingModules: new Set<string>(),
};

export const useModuleSubscriptionStore = create<ModuleSubscriptionState>((set, get) => ({
  ...initialState,

  setOrganizationId: (orgId) => {
    const currentOrgId = get().organizationId;
    if (currentOrgId !== orgId) {
      set({ organizationId: orgId, subscriptions: [] });
      if (orgId) {
        get().fetchSubscriptions();
      }
    }
  },

  fetchSubscriptions: async () => {
    const orgId = get().organizationId;
    if (!orgId) {
      set({ subscriptions: [], isLoading: false });
      return;
    }

    try {
      set({ isLoading: true });

      // Jedno pytanie, jedna odpowiedź: o listę organów pyta się bazy, a nie
      // składa jej z zapytania po `module_subscriptions` plus filtra po
      // datach na kliencie. Ten filtr nie znał `subscription_ends_at`, czyli
      // pola, którym `module_subscription_set_enabled` (467:227) gasi moduł.
      const { data, error } = await supabase.rpc('moduly_z_dostepem', {
        p_organization_id: orgId,
      });

      if (error) throw error;

      set({ subscriptions: (data as ModuleSubscription[] | null) ?? [], isLoading: false });
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      set({ subscriptions: [], isLoading: false });
    }
  },

  refreshAfterToggle: async (moduleId, action) => {
    if (action === 'enable') {
      set((state) => ({
        animatingModules: new Set(state.animatingModules).add(moduleId),
      }));
    } else if (action === 'disable') {
      set((state) => ({
        animatingModules: new Set(state.animatingModules).add(moduleId),
      }));
    }

    await get().fetchSubscriptions();

    if (action === 'enable') {
      setTimeout(() => {
        get().clearAnimatingModule(moduleId);
      }, 600);
    }
  },

  clearAnimatingModule: (moduleId) => {
    set((state) => {
      const newSet = new Set(state.animatingModules);
      newSet.delete(moduleId);
      return { animatingModules: newSet };
    });
  },

  reset: () => {
    set({
      subscriptions: [],
      isLoading: false,
      organizationId: null,
      animatingModules: new Set(),
    });
  },
}));

registerStoreReset('moduleSubscription', () => useModuleSubscriptionStore.getState().reset());
